import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  CheckSquare,
  Square,
  Loader2,
  CheckCircle2,
  XCircle,
  Building2,
  Download,
  RefreshCw,
  Receipt,
  FileText,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  ArrowRight,
  Circle,
  Search,
  Calendar,
  X,
} from "lucide-react";


import { BASE_URL } from "../api";

const API_BASE= `${BASE_URL}`;



/**
 * TCS Billing — /billing/tcs-billing
 *
 * UX changes from the previous version (functionality/API calls unchanged):
 *   1. The page is now a 3-step flow — Select Camps -> Configure Billing -> Review & Generate —
 *      instead of one long scrolling page. Only the active step's content renders.
 *   2. A sticky action bar is pinned to the bottom of the viewport on every step, so the
 *      primary action (Initialize / Continue / Generate) is always visible. You never have to
 *      scroll back up to find a button after selecting rows lower down the page.
 *   3. Step 2 (Configure Billing) shows one accordion row per camp instead of stacking every
 *      camp's Days + Rates tables permanently on screen. A status chip shows saved/unsaved state
 *      per camp so you can tell what still needs attention without opening it.
 *
 * Sidebar entry to add to your existing Billing sidebar config:
 *   { name: "TCS Billing", path: "/billing/tcs-billing", icon: "🏢" }
 *
 * Backend endpoints used (per spec) — unchanged:
 *   GET   /api/camps/
 *   POST  /api/tcs/camp/days/                            -> initializes selected camps, returns { data: [...], total_camps }
 *   PATCH /api/tcs/camp/{camp_id}/days/                   -> body: { days: [{ id, mg }] } (modified rows only)
 *   GET   /api/tcs/camp/{camp_id}/service-rate/           -> returns service rate rows for a camp
 *   PATCH /api/tcs/camp/{camp_id}/service-rate/           -> body: { rates: [{ id, rate_per_case }] } (modified rows only)
 *   POST  /api/tcs/preview-bill/                          -> body: { camp_ids: [...] } -> preview rows + totals (no persistence)
 *   POST  /api/tcs/generate-bill/                         -> body: { camp_ids: [...] } -> { invoice_number, invoice_url }
 */


// const API_BASE = "https://cbackend.xraidigital.com";

function pick(obj, keys, fallback = "") {
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== "") return obj[key];
  }
  return fallback;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatCurrency(value) {
  const n = toNumber(value, 0);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Parses a camp's start/end date string into a Date, or null if it's
// missing/unparseable (e.g. the "—" fallback). Used by the date filter only.
function parseDate(value) {
  if (!value || value === "—") return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function deriveStatus(raw) {
  if (raw.status) return raw.status;
  if (raw.is_completed) return "Completed";
  if (raw.ready_to_go) return "Ready to Go";
  return "Pending";
}

function normalizeCamp(raw) {
  return {
    id: pick(raw, ["id", "camp_id", "pk", "uuid"]),
    name: pick(raw, ["client_name", "name", "camp_name", "title", "camp"], "Unnamed Camp"),
    location: pick(raw, ["location", "location_name", "site", "address"], "—"),
    district: pick(raw, ["district"], ""),
    state: pick(raw, ["state"], ""),
    start_date: pick(raw, ["start_date", "startDate", "from_date"], "—"),
    end_date: pick(raw, ["end_date", "endDate", "to_date"], "—"),
    billing_mode: pick(raw, ["billing_mode", "billingMode", "mode"], ""),
    status: deriveStatus(raw),
    isNorthstar: !!raw.is_northstar,
    _raw: raw,
  };
}

function normalizeServiceRate(raw) {
  const rate = pick(raw, ["rate_per_case", "ratePerCase", "rate"], 0);
  return {
    id: pick(raw, ["id", "service_id", "rate_id", "pk"]),
    serviceName: pick(raw, ["service_name", "serviceName", "name", "service"], "Unnamed Service"),
    rate,
    originalRate: rate,
  };
}

function normalizePreviewRow(raw) {
  return {
    camp: pick(raw, ["camp_location", "camp_name", "campName", "camp"], "—"),
    service: pick(raw, ["modality", "service_name", "serviceName", "service"], "—"),
    actualCases: pick(raw, ["actual_cases", "actualCases"], 0),
    billableCases: pick(raw, ["billable_cases", "billableCases"], 0),
    rate: pick(raw, ["rate", "rate_per_case"], 0),
    amount: pick(raw, ["amount", "total"], 0),
    period: pick(raw, ["period"], ""),
  };
}

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------
function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [toast, onClose]);

  if (!toast) return null;

  const isError = toast.type === "error";
  return (
    <div
      className={`fixed top-5 right-5 z-[60] flex items-start gap-3 rounded-xl border px-4 py-3 shadow-lg max-w-sm ${
        isError ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"
      }`}
      role="status"
    >
      {isError ? (
        <XCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
      ) : (
        <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
      )}
      <div className={`text-sm font-medium ${isError ? "text-red-800" : "text-emerald-800"}`}>
        {toast.message}
      </div>
    </div>
  );
}

const getAuthHeader = (token) => {
  if (!token) return {};
  if (token.split(".").length === 3) return { Authorization: `Bearer ${token}` };
  return { Authorization: `Token ${token}` };
};

const apiRequest = async (url, options = {}) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("User not logged in");

  const headers = {
    ...getAuthHeader(token),
    ...(options.body && !(options.body instanceof FormData) ? { "Content-Type": "application/json" } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401) {
    localStorage.removeItem("token");
    throw new Error("Session expired. Please login again.");
  }
  if (!res.ok) {
    let msg = "Request failed";
    try {
      const err = await res.json();
      msg = err.detail || JSON.stringify(err);
    } catch {
      msg = await res.text();
    }
    throw new Error(msg);
  }
  return res.status === 204 ? null : res.json();
};

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------
const STEPS = [
  { key: "select", label: "Select Camps" },
  { key: "configure", label: "Configure Billing" },
  { key: "review", label: "Review & Generate" },
];

function StepIndicator({ currentStep, furthestStep, onJump }) {
  const currentIdx = STEPS.findIndex((s) => s.key === currentStep);
  const furthestIdx = STEPS.findIndex((s) => s.key === furthestStep);

  return (
    <div className="flex items-center gap-2 sm:gap-4">
      {STEPS.map((step, idx) => {
        const isActive = step.key === currentStep;
        const isDone = idx < currentIdx;
        const isReachable = idx <= furthestIdx;
        return (
          <React.Fragment key={step.key}>
            <button
              onClick={() => isReachable && onJump(step.key)}
              disabled={!isReachable}
              className={`flex items-center gap-2 rounded-full pl-1.5 pr-3 py-1.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-white text-indigo-700"
                  : isReachable
                  ? "text-indigo-100 hover:text-white"
                  : "text-indigo-300 cursor-not-allowed"
              }`}
            >
              <span
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                  isActive
                    ? "bg-indigo-600 text-white"
                    : isDone
                    ? "bg-emerald-400 text-white"
                    : "bg-indigo-400/40 text-indigo-100"
                }`}
              >
                {isDone ? <CheckCircle2 className="w-3.5 h-3.5" /> : idx + 1}
              </span>
              <span className="hidden sm:inline">{step.label}</span>
            </button>
            {idx < STEPS.length - 1 && <div className="w-4 sm:w-8 h-px bg-indigo-300/50" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sticky action bar — always visible at the bottom of the viewport so the
// primary action for the current step never requires scrolling back up.
// ---------------------------------------------------------------------------
function StickyActionBar({ children }) {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur px-6 py-3 shadow-[0_-4px_16px_rgba(15,23,42,0.06)]">
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function TCSBillingPage() {
  const [currentStep, setCurrentStep] = useState("select"); // 'select' | 'configure' | 'review'
  const [furthestStep, setFurthestStep] = useState("select");

  const [camps, setCamps] = useState([]);
  const [loadingCamps, setLoadingCamps] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Step 1 search + date filter — filters the camp list client-side,
  // doesn't touch the underlying `camps` data or trigger a refetch.
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [initializing, setInitializing] = useState(false);

  // campDays: { [campId]: { campName, days: [{id, date, mg, originalMg}] } }
  const [campDays, setCampDays] = useState({});
  const [dirtyRows, setDirtyRows] = useState({});
  const [savingCampId, setSavingCampId] = useState(null);

  // serviceRates: { [campId]: { rates: [...] } }
  const [serviceRates, setServiceRates] = useState({});
  const [ratesLoading, setRatesLoading] = useState({});
  const [dirtyRateRows, setDirtyRateRows] = useState({});
  const [savingRatesCampId, setSavingRatesCampId] = useState(null);

  // Which camp is expanded in the Step 2 accordion (only one at a time,
  // so the page doesn't turn into a wall of tables again).
  const [expandedCampId, setExpandedCampId] = useState(null);

  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [invoiceUrl, setInvoiceUrl] = useState(null);

  const [toast, setToast] = useState(null);
  const notify = (message, type = "success") => setToast({ message, type });

  const goToStep = (step) => {
    setCurrentStep(step);
    const idx = STEPS.findIndex((s) => s.key === step);
    const furthestIdx = STEPS.findIndex((s) => s.key === furthestStep);
    if (idx > furthestIdx) setFurthestStep(step);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // -------------------------------------------------------------------------
  // Step 1 — Load camps
  // -------------------------------------------------------------------------
  const loadCamps = useCallback(async () => {
    setLoadingCamps(true);
    try {
      const data = await apiRequest(`${API_BASE}/api/billing-camps/`);
      const list = Array.isArray(data) ? data : data.results || [];
      setCamps(list.map(normalizeCamp));
    } catch (err) {
      notify(err.message || "Could not load camps", "error");
      setCamps([]);
    } finally {
      setLoadingCamps(false);
    }
  }, []);

  useEffect(() => {
    loadCamps();
  }, [loadCamps]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Camps visible in Step 1 after search text + date range are applied.
  // A camp matches the date range if its [start_date, end_date] window
  // overlaps the [dateFrom, dateTo] window at all (partial overlap counts).
  const filteredCamps = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo) : null;

    return camps.filter((camp) => {
      // Only TCS Monthly camps belong on this page. If your /api/billing-camps/
      // response doesn't set billing_mode until after a camp is initialized,
      // this will hide camps before they're ready — see the note below.
      if (camp.billing_mode !== "TCS_MONTHLY") return false;

      if (query) {
        const haystack = `${camp.name} ${camp.location} ${camp.district} ${camp.state}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }

      if (from || to) {
        const campStart = parseDate(camp.start_date);
        const campEnd = parseDate(camp.end_date) || campStart;
        // If the camp has no usable dates at all, exclude it once a date
        // filter is active rather than silently showing an unfilterable row.
        if (!campStart) return false;
        if (from && campEnd && campEnd < from) return false;
        if (to && campStart > to) return false;
      }

      return true;
    });
  }, [camps, searchQuery, dateFrom, dateTo]);

  const hasActiveFilters = searchQuery.trim() !== "" || dateFrom !== "" || dateTo !== "";
  const clearFilters = () => {
    setSearchQuery("");
    setDateFrom("");
    setDateTo("");
  };

  const allSelected = filteredCamps.length > 0 && filteredCamps.every((c) => selectedIds.has(c.id));
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        filteredCamps.forEach((c) => next.delete(c.id));
      } else {
        filteredCamps.forEach((c) => next.add(c.id));
      }
      return next;
    });
  };

  // -------------------------------------------------------------------------
  // Service rates — load, edit, save (per camp)
  // -------------------------------------------------------------------------
  const loadServiceRates = useCallback(async (campId) => {
    setRatesLoading((prev) => ({ ...prev, [campId]: true }));
    try {
      const res = await fetch(`${API_BASE}/api/tcs/camp/${campId}/service-rate/`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.services || data.results || data.data || [];
      setServiceRates((prev) => ({ ...prev, [campId]: { rates: list.map(normalizeServiceRate) } }));
    } catch {
      notify("Could not load service rates for a camp", "error");
      setServiceRates((prev) => ({ ...prev, [campId]: { rates: [] } }));
    } finally {
      setRatesLoading((prev) => ({ ...prev, [campId]: false }));
    }
  }, []);

  const editRate = (campId, rateId, value) => {
    setServiceRates((prev) => {
      const camp = prev[campId];
      if (!camp) return prev;
      const rates = camp.rates.map((r) => (r.id === rateId ? { ...r, rate: value } : r));
      return { ...prev, [campId]: { ...camp, rates } };
    });
    setDirtyRateRows((prev) => {
      const set = new Set(prev[campId] || []);
      set.add(rateId);
      return { ...prev, [campId]: set };
    });
  };

  const saveServiceRates = async (campId) => {
    const camp = serviceRates[campId];
    const dirty = dirtyRateRows[campId];
    if (!camp || !dirty || dirty.size === 0) {
      notify("No rate changes to save", "error");
      return;
    }
    const modifiedRows = camp.rates.filter((r) => dirty.has(r.id));

    setSavingRatesCampId(campId);
    try {
      const res = await fetch(`${API_BASE}/api/tcs/camp/${campId}/service-rate/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          services: modifiedRows.map((r) => ({ id: r.id, rate_per_case: toNumber(r.rate) })),
        }),
      });
      if (!res.ok) throw new Error();

      setServiceRates((prev) => ({
        ...prev,
        [campId]: { ...prev[campId], rates: prev[campId].rates.map((r) => ({ ...r, originalRate: r.rate })) },
      }));
      setDirtyRateRows((prev) => ({ ...prev, [campId]: new Set() }));
      notify("Service Rates Updated Successfully");
    } catch {
      notify("Could not save service rates. Try again.", "error");
    } finally {
      setSavingRatesCampId(null);
    }
  };

  // -------------------------------------------------------------------------
  // Step 2 (backend) — Initialize TCS camp days
  // -------------------------------------------------------------------------
  const initializeCampDays = async () => {
    if (selectedIds.size === 0) {
      notify("Select at least one camp first", "error");
      return;
    }

    setInitializing(true);
    setServiceRates({});
    setDirtyRateRows({});
    setPreviewData(null);
    setInvoiceNumber(null);
    setInvoiceUrl(null);

    try {
      const res = await fetch(`${API_BASE}/api/tcs/camp/days/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ camp_ids: [...selectedIds] }),
      });
      if (!res.ok) throw new Error("Failed to initialize camps");

      const result = await res.json();
      const nextCampDays = {};
      result.data.forEach((camp) => {
        nextCampDays[camp.camp_id] = {
          campName: camp.camp_name,
          days: camp.days.map((day) => ({
              id: day.id,
              date: day.date,
              mg: day.daily_mg,
              originalMg: day.daily_mg,

              // Add these two fields
              is_conducted: day.is_conducted,
              has_patients: day.has_patients,
          })),
        };
      });

      setCampDays(nextCampDays);
      const firstCampId = Object.keys(nextCampDays)[0] || null;
      setExpandedCampId(firstCampId);

      notify(`${result.total_camps} camp(s) initialized successfully.`);
      loadCamps();

      Object.keys(nextCampDays).forEach((campId) => loadServiceRates(campId));

      // Move straight into the Configure step — this is the step that used
      // to require scrolling back up to reach.
      goToStep("configure");
    } catch (err) {
      console.error(err);
      notify("Unable to initialize camps.", "error");
    } finally {
      setInitializing(false);
    }
  };

  // -------------------------------------------------------------------------
  // Daily MG edit/save
  // -------------------------------------------------------------------------
  const editMg = (campId, dayId, value) => {
    setCampDays((prev) => {
      const camp = prev[campId];
      if (!camp) return prev;
      const days = camp.days.map((d) => (d.id === dayId ? { ...d, mg: value } : d));
      return { ...prev, [campId]: { ...camp, days } };
    });
    setDirtyRows((prev) => {
      const set = new Set(prev[campId] || []);
      set.add(dayId);
      return { ...prev, [campId]: set };
    });
  };

  // const editConducted = (campId, dayId, value) => {

  //     setCampDays(prev => ({

  //         ...prev,

  //         [campId]: {

  //             ...prev[campId],

  //             days: prev[campId].days.map(day =>
  //                 day.id === dayId
  //                     ? {
  //                         ...day,
  //                         is_conducted: value,
  //                     }
  //                     : day
  //             )
  //         }

  //     }));

  //     setDirtyRows(prev => {

  //         const s = new Set(prev[campId] || []);

  //         s.add(dayId);

  //         return {
  //             ...prev,
  //             [campId]: s,
  //         };

  //     });

  // };

  const saveMg = async (campId) => {
    const camp = campDays[campId];
    const dirty = dirtyRows[campId];
    if (!camp || !dirty || dirty.size === 0) {
      notify("No changes to save", "error");
      return;
    }
    const modifiedRows = camp.days.filter((d) => dirty.has(d.id));

    setSavingCampId(campId);
    try {
      const res = await fetch(`${API_BASE}/api/tcs/camp/${campId}/daily-mg/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            days: modifiedRows.map((d) => ({
                id: d.id,
                daily_mg: Number(d.mg),
                // is_conducted: d.is_conducted,
            })),
        }),
      });
      if (!res.ok) throw new Error();

      setCampDays((prev) => ({
        ...prev,
        [campId]: { ...prev[campId], days: prev[campId].days.map((d) => ({ ...d, originalMg: d.mg })) },
      }));
      setDirtyRows((prev) => ({ ...prev, [campId]: new Set() }));
      notify("Daily MG Updated Successfully");
    } catch {
      notify("Could not save Daily MG. Try again.", "error");
    } finally {
      setSavingCampId(null);
    }
  };

  // -------------------------------------------------------------------------
  // Preview + Generate
  // -------------------------------------------------------------------------
  const previewMonthlyBill = async () => {
    const campIds = Object.keys(campDays);
    if (campIds.length === 0) {
      notify("Initialize at least one camp before previewing a bill", "error");
      return;
    }

    setPreviewing(true);
    try {
      const res = await fetch(`${API_BASE}/api/tcs/preview-bill/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ camp_ids: campIds.map((id) => (Number.isNaN(Number(id)) ? id : Number(id))) }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.rows || data.data || [];
      const rows = list.map(normalizePreviewRow);

      const totalsRaw = data.summary || {};
      const totals = {
        actualCases: toNumber(
          pick(totalsRaw, ["actual_cases", "actualCases"], null),
          rows.reduce((sum, r) => sum + toNumber(r.actualCases), 0)
        ),
        billableCases: toNumber(
          pick(totalsRaw, ["billable_cases", "billableCases"], null),
          rows.reduce((sum, r) => sum + toNumber(r.billableCases), 0)
        ),
        final_total: toNumber(
          pick(totalsRaw, ["final_total", "finalTotal"], null),
          rows.reduce((sum, r) => sum + toNumber(r.amount), 0)
        ),
      };

      setPreviewData({ rows, totals });
    } catch {
      notify("Could not generate the bill preview. Try again.", "error");
    } finally {
      setPreviewing(false);
    }
  };

  const generateMonthlyBill = async () => {
    const campIds = Object.keys(campDays);
    if (campIds.length === 0) {
      notify("Initialize at least one camp before generating a bill", "error");
      return;
    }

    setGeneratingInvoice(true);
    try {
      const res = await fetch(`${API_BASE}/api/tcs/generate-bill/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ camp_ids: campIds.map((id) => (Number.isNaN(Number(id)) ? id : Number(id))) }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();

      setInvoiceNumber(pick(data, ["invoice_number", "invoiceNumber"], null));
      setInvoiceUrl(data.invoice_url);
      notify("Monthly Bill Generated Successfully");

      if (data.invoice_url) window.open(data.invoice_url, "_blank", "noopener,noreferrer");
    } catch {
      notify("Could not generate the monthly bill. Try again.", "error");
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const campIdsWithDays = useMemo(() => Object.keys(campDays), [campDays]);

  // A camp counts as "ready" once its MG rows and rate rows have no unsaved edits.
  const campStatus = (campId) => {
    const mgDirty = (dirtyRows[campId] || new Set()).size > 0;
    const rateDirty = (dirtyRateRows[campId] || new Set()).size > 0;
    if (mgDirty || rateDirty) return "unsaved";
    return "saved";
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-24" style={{ fontFamily: "Inter, sans-serif" }}>
      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* Sticky header with step indicator */}
      <div className="sticky top-0 z-30 bg-[#0D3366] text-white shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold leading-tight">
                TCS Monthly Billing
              </h1>
              <p className="text-indigo-100 text-xs">
                Select camps, configure rates, then generate one consolidated bill.
              </p>
            </div>
          </div>

          <StepIndicator
            currentStep={currentStep}
            furthestStep={furthestStep}
            onJump={goToStep}
          />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* ------------------------------------------------------------- */}
        {/* STEP 1 — Select Camps                                         */}
        {/* ------------------------------------------------------------- */}
        {currentStep === "select" && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 bg-slate-900 text-white">
              <h2 className="font-medium">1. Choose which camps to bill</h2>
              <button
                onClick={loadCamps}
                className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white transition-colors"
                disabled={loadingCamps}
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loadingCamps ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>

            {/* Search + date filter row */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-6 py-4 border-b border-slate-100 bg-slate-50/60">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by camp name or location..."
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                />
              </div>

              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400 hidden sm:block" />
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  aria-label="From date"
                  className="px-2.5 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                />
                <span className="text-slate-400 text-sm">to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  aria-label="To date"
                  className="px-2.5 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                />
              </div>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 px-2 py-2"
                >
                  <X className="w-3.5 h-3.5" />
                  Clear
                </button>
              )}
            </div>

            {filteredCamps.length !== camps.length && (
              <div className="px-6 py-2 text-xs text-slate-500 bg-white border-b border-slate-100">
                Showing {filteredCamps.length} of {camps.length} camps
                {" · "}TCS Monthly only{hasActiveFilters ? " + your filters" : ""}
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-left">
                    <th className="px-6 py-3 font-medium w-10">
                      <button onClick={toggleSelectAll} className="flex items-center" aria-label="Select all">
                        {allSelected ? (
                          <CheckSquare className="w-4 h-4 text-indigo-600" />
                        ) : (
                          <Square className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                    </th>
                    <th className="px-4 py-3 font-medium">Camp Name</th>
                    <th className="px-4 py-3 font-medium">Location</th>
                    <th className="px-4 py-3 font-medium">Start Date</th>
                    <th className="px-4 py-3 font-medium">End Date</th>
                    <th className="px-4 py-3 font-medium">Billing Mode</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingCamps ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-10 text-center text-slate-400">
                        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                        Loading camps...
                      </td>
                    </tr>
                  ) : filteredCamps.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-10 text-center text-slate-400">
                        {hasActiveFilters ? (
                          <>
                            No TCS Monthly camps match your search or date range.{" "}
                            <button className="text-indigo-600 font-medium underline" onClick={clearFilters}>
                              Clear filters
                            </button>
                          </>
                        ) : (
                          "No camps with billing mode TCS Monthly found."
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredCamps.map((camp) => (
                      <tr
                        key={camp.id}
                        onClick={() => toggleSelect(camp.id)}
                        className={`border-t border-slate-100 cursor-pointer transition-colors ${
                          selectedIds.has(camp.id) ? "bg-indigo-50/60 hover:bg-indigo-50" : "hover:bg-slate-50/60"
                        }`}
                      >
                        <td className="px-6 py-3" onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => toggleSelect(camp.id)} aria-label={`Select ${camp.name}`}>
                            {selectedIds.has(camp.id) ? (
                              <CheckSquare className="w-4 h-4 text-indigo-600" />
                            ) : (
                              <Square className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-800">
                          <div className="flex items-center gap-1.5">
                            {camp.name}
                            {camp.isNorthstar && (
                              <span
                                title="Northstar camp"
                                className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700"
                              >
                                ★ Northstar
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{camp.location}</td>
                        <td className="px-4 py-3 text-slate-600">{camp.start_date}</td>
                        <td className="px-4 py-3 text-slate-600">{camp.end_date}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              camp.billing_mode === "TCS_MONTHLY"
                                ? "bg-indigo-100 text-indigo-700"
                                : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {camp.billing_mode || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              camp.status === "Completed"
                                ? "bg-emerald-100 text-emerald-700"
                                : camp.status === "Ready to Go"
                                ? "bg-indigo-100 text-indigo-700"
                                : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {camp.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ------------------------------------------------------------- */}
        {/* STEP 2 — Configure Billing (accordion, one camp at a time)     */}
        {/* ------------------------------------------------------------- */}
        {currentStep === "configure" && (
          <div>
            {campIdsWithDays.length === 0 ? (
              <div className="text-center text-slate-400 text-sm py-10 border border-dashed border-slate-200 rounded-2xl bg-white">
                No camps initialized yet. Go back to{" "}
                <button className="text-indigo-600 font-medium underline" onClick={() => goToStep("select")}>
                  Select Camps
                </button>{" "}
                first.
              </div>
            ) : (
              <div className="space-y-3">
                {campIdsWithDays.map((campId) => {
                  const camp = campDays[campId];
                  const isExpanded = expandedCampId === campId;
                  const dirty = dirtyRows[campId] || new Set();
                  const isSaving = savingCampId === campId;

                  const rates = serviceRates[campId]?.rates || [];
                  const ratesAreLoading = !!ratesLoading[campId];
                  const dirtyRates = dirtyRateRows[campId] || new Set();
                  const isSavingRates = savingRatesCampId === campId;
                  const status = campStatus(campId);

                  return (
                    <div key={campId} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                      <button
                        onClick={() => setExpandedCampId(isExpanded ? null : campId)}
                        className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          )}
                          <span className="font-medium text-slate-800">{camp.campName}</span>
                        </div>
                        <span
                          className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${
                            status === "saved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {status === "saved" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                          {status === "saved" ? "Up to date" : "Unsaved changes"}
                        </span>
                      </button>

                      {isExpanded && (
                        <div className="border-t border-slate-100 px-6 py-5 space-y-6">
                          {/* Daily MG */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h3 className="text-sm font-semibold text-slate-700">Camp Days — Daily MG</h3>
                              <button
                                onClick={() => saveMg(campId)}
                                disabled={isSaving || dirty.size === 0}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
                              >
                                {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                Save MG
                              </button>
                            </div>
                            <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
                              <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-slate-50">
                                  <tr className="text-slate-500 text-left">
                                    <th className="px-4 py-2.5 font-medium">Date</th>
                                    <th className="px-4 py-2.5 font-medium">Conducted</th>
                                    <th className="px-4 py-2.5 font-medium">Daily MG</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {camp.days.map((day) => (
                                    <tr key={day.id} className="border-t border-slate-100">

                                      <td className="px-4 py-2 text-slate-700">
                                        {day.date}
                                      </td>

                                      <td className="px-4 py-2">
                                        {day.is_conducted || day.has_patients ? (
                                          <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                                            Conducted
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700">
                                            Not Conducted
                                          </span>
                                        )}
                                      </td>

                                      <td className="px-4 py-2">

                                        <input
                                          type="number"
                                          value={day.mg}
                                          onChange={(e) =>
                                            editMg(campId, day.id, e.target.value)
                                          }
                                          className={`w-24 px-2 py-1 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                                            dirty.has(day.id)
                                              ? "border-amber-300 bg-amber-50"
                                              : "border-slate-200"
                                          }`}
                                        />

                                      </td>

                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Service rates */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h3 className="text-sm font-semibold text-slate-700">Service Rates</h3>
                              <button
                                onClick={() => saveServiceRates(campId)}
                                disabled={isSavingRates || dirtyRates.size === 0}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
                              >
                                {isSavingRates && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                Save Service Rates
                              </button>
                            </div>
                            <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
                              <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-slate-50">
                                  <tr className="text-slate-500 text-left">
                                    <th className="px-4 py-2.5 font-medium">Service Name</th>
                                    <th className="px-4 py-2.5 font-medium">Rate Per Case</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {ratesAreLoading ? (
                                    <tr>
                                      <td colSpan={2} className="px-4 py-8 text-center text-slate-400">
                                        <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                                        Loading service rates...
                                      </td>
                                    </tr>
                                  ) : rates.length === 0 ? (
                                    <tr>
                                      <td colSpan={2} className="px-4 py-8 text-center text-slate-400">
                                        No service rates found for this camp.
                                      </td>
                                    </tr>
                                  ) : (
                                    rates.map((rate) => (
                                      <tr key={rate.id} className="border-t border-slate-100">
                                        <td className="px-4 py-2 text-slate-700">{rate.serviceName}</td>
                                        <td className="px-4 py-2">
                                          <input
                                            type="number"
                                            value={rate.rate}
                                            onChange={(e) => editRate(campId, rate.id, e.target.value)}
                                            className={`w-28 px-2 py-1 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                                              dirtyRates.has(rate.id) ? "border-amber-300 bg-amber-50" : "border-slate-200"
                                            }`}
                                          />
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ------------------------------------------------------------- */}
        {/* STEP 3 — Review & Generate                                     */}
        {/* ------------------------------------------------------------- */}
        {currentStep === "review" && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 px-6 py-6">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-medium text-slate-800">3. Preview, then generate the consolidated bill</h2>
              <span className="text-sm text-slate-400">
                {campIdsWithDays.length} camp{campIdsWithDays.length === 1 ? "" : "s"} included
              </span>
            </div>
            <p className="text-sm text-slate-500 mb-4">Previewing does not save anything — check the numbers, then generate when ready.</p>

            <button
              onClick={previewMonthlyBill}
              disabled={previewing}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
            >
              {previewing && <Loader2 className="w-4 h-4 animate-spin" />}
              {previewing ? "Loading Preview..." : "Preview Monthly Bill"}
            </button>

            {previewData && (
              <div className="mt-5 border-t border-slate-100 pt-5">
                <div className="flex items-center gap-2 mb-3 text-slate-700">
                  <Receipt className="w-4 h-4 text-indigo-600" />
                  <h3 className="font-medium">Bill Preview</h3>
                </div>

                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 text-left">
                        <th className="px-4 py-3 font-medium">Camp</th>
                        <th className="px-4 py-3 font-medium">Service</th>
                        <th className="px-4 py-3 font-medium text-right">Actual Cases</th>
                        <th className="px-4 py-3 font-medium text-right">Billable Cases</th>
                        <th className="px-4 py-3 font-medium text-right">Rate</th>
                        <th className="px-4 py-3 font-medium text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.rows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                            No billable rows returned for the selected camps.
                          </td>
                        </tr>
                      ) : (
                        previewData.rows.map((row, idx) => (
                          <tr key={idx} className="border-t border-slate-100">
                            <td className="px-4 py-2.5 text-slate-700">{row.camp}</td>
                            <td className="px-4 py-2.5 text-slate-700">{row.service}</td>
                            <td className="px-4 py-2.5 text-right text-slate-600">{row.actualCases}</td>
                            <td className="px-4 py-2.5 text-right text-slate-600">{row.billableCases}</td>
                            <td className="px-4 py-2.5 text-right text-slate-600">{formatCurrency(row.rate)}</td>
                            <td className="px-4 py-2.5 text-right font-medium text-slate-800">
                              {formatCurrency(row.amount)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 sm:gap-8 rounded-lg bg-slate-50 border border-slate-200 px-5 py-4">
                  <div className="text-sm text-slate-500">
                    Actual Cases: <span className="font-semibold text-slate-800">{previewData.totals.actualCases}</span>
                  </div>
                  <div className="text-sm text-slate-500">
                    Billable Cases: <span className="font-semibold text-slate-800">{previewData.totals.billableCases}</span>
                  </div>
                  <div className="text-sm text-slate-500">
                    Grand Total:{" "}
                    <span className="font-semibold text-indigo-700">₹{formatCurrency(previewData.totals.final_total)}</span>
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <button
                    onClick={generateMonthlyBill}
                    disabled={generatingInvoice}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {generatingInvoice && <Loader2 className="w-4 h-4 animate-spin" />}
                    {generatingInvoice ? "Generating Monthly Bill..." : "Generate Monthly Bill"}
                  </button>

                  {invoiceUrl && (
                    <a
                      href={invoiceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 rounded-lg border border-indigo-200 text-indigo-700 text-sm font-medium hover:bg-indigo-50 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download Invoice
                    </a>
                  )}
                </div>

                {invoiceNumber && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700">
                    <FileText className="w-4 h-4" />
                    Invoice Number: <span className="font-semibold">{invoiceNumber}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------- */}
      {/* Sticky action bar — the fix for "select then scroll up to act" */}
      {/* ------------------------------------------------------------- */}
      {currentStep === "select" && (
        <StickyActionBar>
          <span className="text-sm text-slate-500">
            {selectedIds.size === 0
              ? "Tick the camps you want to bill this month."
              : `${selectedIds.size} camp${selectedIds.size === 1 ? "" : "s"} selected`}
          </span>
          <button
            onClick={initializeCampDays}
            disabled={initializing || selectedIds.size === 0}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
          >
            {initializing && <Loader2 className="w-4 h-4 animate-spin" />}
            {initializing ? "Initializing Camp Days..." : "Initialize TCS Camp Days"}
            {!initializing && <ArrowRight className="w-4 h-4" />}
          </button>
        </StickyActionBar>
      )}

      {currentStep === "configure" && (
        <StickyActionBar>
          <button
            onClick={() => goToStep("select")}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Select Camps
          </button>
          <span className="text-sm text-slate-500 hidden sm:inline">
            {campIdsWithDays.filter((id) => campStatus(id) === "unsaved").length > 0
              ? "Some camps have unsaved changes."
              : "All camps are up to date."}
          </span>
          <button
            onClick={() => goToStep("review")}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            Continue to Review
            <ArrowRight className="w-4 h-4" />
          </button>
        </StickyActionBar>
      )}

      {currentStep === "review" && (
        <StickyActionBar>
          <button
            onClick={() => goToStep("configure")}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Configure
          </button>
          <span className="text-sm text-slate-500 hidden sm:inline">
            {previewData ? "Preview loaded — ready to generate when you are." : "Run a preview before generating."}
          </span>
        </StickyActionBar>
      )}
    </div>
  );
}